'use client'; // This directive tells Next.js to treat this file as a client component

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Fragment, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Scrollbar } from 'swiper/modules';
import { useAuth } from '@clerk/nextjs';
import { toast } from 'react-toastify';
import Image from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';

import 'swiper/css';

import {
  mapProductAlphaSizeToString,
  mapProductCategoryToString,
  mapProductMaterialsToString,
  mapProductSizeTypeToString,
  mapProductSubCategoryToString,
} from '@lib/product/constants';
import { Separator } from '@components/common/separator';
import { useCartStore } from '@lib/stores/useCartStore';
import { addProductToCart } from '@lib/cart/actions';
import { Button } from '@components/common/button';
import { Product } from '@lib/product/definitions';
import { Badge } from '@components/common/badge';
import { capitalizedPerWord } from '@lib/utils';
import { customToast } from '@lib/toast/custom';
import { handleLog } from '@lib/logging/utils';
import { User } from '@lib/user/definitions';
import { CacheTags } from '@lib/tags';
import { Routes } from '@lib/routes';

interface ProductLandingProps {
  product: Product;
  seller: User;
}

export function ProductLanding({
  product,
  seller,
}: Readonly<ProductLandingProps>) {
  const { cart, setCart } = useCartStore();
  const { isSignedIn } = useAuth();
  const [loading, startTransition] = useTransition();
  const { push } = useRouter();

  const handleAddToCart = () => {
    // Allow user to add to cart when they are signed-in
    if (!isSignedIn) {
      customToast({
        title: 'Please log in to continue',
        content: 'You can only add product to your cart once you’re signed in',
        type: 'info',
      });
      push(Routes.signIn);
      return;
    }

    if (!cart) {
      toast.error('Oops! Something went wrong. Please try again later.');
      handleLog('error', {
        message: 'Undefined Cart',
      });
      return;
    }

    startTransition(() => {
      addProductToCart({
        cartId: cart.id,
        productId: product.id,
        tags: [CacheTags.Cart],
      })
        .then((newCart) => {
          setCart(newCart);
          toast.success(
            `Product ${capitalizedPerWord(product.name)} added to cart!`,
          );
        })
        .catch((error) => {
          console.error('addProductToCart error: ', error);
          toast.error((error as Error).message);
        });
    });
  };

  return (
    <>
      {product.images && (
        <>
          {/* Mobile Slider */}
          <div className='sm:hidden aspect-square rounded-md overflow-hidden'>
            <Swiper
              slidesPerView={1}
              modules={[Scrollbar]}
              spaceBetween={8}
              className='size-full'
            >
              {product.images
                .filter((image) => image?.imageUrl)
                .map((image, index) => (
                  <SwiperSlide
                    key={image.imageAltText || `product-image-${index}`}
                    className='relative'
                  >
                    <Image
                      src={image.imageUrl}
                      alt={image.imageAltText}
                      fill
                      sizes='(max-width: 640px) 100vw, 80vw'
                      priority={index === 0}
                      className='object-cover object-center rounded-md'
                    />
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>

          {/* Desktop Tabs */}
          <div className='hidden sm:block'>
            <TabGroup as='div' className='flex flex-col'>
              <TabPanels className='aspect-h-1 aspect-w-1'>
                {product.images
                  .filter((image) => image?.imageUrl)
                  .map((image, index) => (
                    <TabPanel key={index} className='outline-none'>
                      <Image
                        src={image.imageUrl}
                        alt={image.imageAltText}
                        fill
                        sizes='(max-width: 50dvw) 100dvw'
                        className='h-full w-full object-cover object-center sm:rounded-lg'
                      />
                    </TabPanel>
                  ))}
              </TabPanels>
              <div className='mt-6 w-full max-w-2xl lg:max-w-none'>
                <TabList className='flex items-center gap-6 w-full'>
                  {product.images
                    .filter((image) => image?.imageUrl)
                    .map((image, index) => (
                      <Tab
                        key={index}
                        className='relative flex-center h-24 sm:h-32 cursor-pointer rounded-md text-sm font-medium uppercase text-gray-900 aspect-square hover:opacity-90'
                      >
                        {({ selected }) => (
                          <>
                            <span className='sr-only'>
                              {image.imageAltText}
                            </span>
                            <span className='absolute inset-0 overflow-hidden rounded-md'>
                              <Image
                                src={image.imageUrl}
                                alt={image.imageAltText}
                                fill
                                sizes='25dvw'
                                priority
                                className='h-full w-full object-cover object-center'
                              />
                            </span>
                            <span
                              className={clsx(
                                selected
                                  ? 'ring-primary/50'
                                  : 'ring-transparent',
                                'pointer-events-none absolute inset-0 rounded-md ring-2 ring-offset-2',
                              )}
                              aria-hidden='true'
                            />
                          </>
                        )}
                      </Tab>
                    ))}
                </TabList>
              </div>
            </TabGroup>
          </div>
        </>
      )}

      <div className='mt-6 sm:mt-16 sm:px-0 lg:mt-0'>
        <div className='flex items-center gap-x-2'>
          <h1 className='text-4xl font-bold tracking-none'>{product.name}</h1>
          {product.isSold && <Badge variant='accent'>Sold</Badge>}
        </div>

        <div className='mt-2 space-y-1'>
          <h2 className='sr-only'>Product information</h2>
          <div className='flex items-center gap-x-2'>
            <span className='text-2xl'>${product?.totalPrice.toFixed(2)}</span>
            <span className='font-medium text-foreground/60'>
              Includes Standard Shipping
            </span>
          </div>
        </div>

        <div className='-space-y-1'>
          <Button
            asChild
            variant='link'
            className='text-base lg:text-lg block p-0 size-auto w-fit'
          >
            <Link href={`${Routes.shop}/${seller.username}`}>
              {seller.shopName || capitalizedPerWord(seller.username)}
            </Link>
          </Button>
          <span className='font-medium text-sm text-foreground/60 block'>
            @{seller.username}
          </span>
        </div>

        <Button
          type='button'
          onClick={handleAddToCart}
          className='max-w-xs flex-1 mt-6'
          disabled={loading || product.isSold}
        >
          {loading ? 'Adding...' : 'Add to cart'}
        </Button>

        <section aria-labelledby='details-heading' className='mt-12'>
          <h2 id='details-heading' className='sr-only'>
            Additional details
          </h2>
          <Separator />
          <div className='divide-y divide-gray-200'>
            <Disclosure as='div' defaultOpen={true}>
              {({ open }) => (
                <>
                  <h3>
                    <DisclosureButton className='group relative flex w-full items-center justify-between py-6 text-left'>
                      <span
                        className={clsx(
                          open ? 'text-foreground' : 'text-gray-900',
                          'text-sm font-medium',
                        )}
                      >
                        Product Story
                      </span>
                      <span className='ml-6 flex items-center'>
                        {open ? (
                          <MinusIcon
                            className='block h-6 w-6 text-indigo-400 group-hover:text-indigo-500'
                            aria-hidden='true'
                          />
                        ) : (
                          <PlusIcon
                            className='block h-6 w-6 text-gray-400 group-hover:text-gray-500'
                            aria-hidden='true'
                          />
                        )}
                      </span>
                    </DisclosureButton>
                  </h3>
                  <DisclosurePanel as='div' className='prose prose-sm pb-6'>
                    <div
                      className='space-y-6 text-base text-gray-700 whitespace-pre-line'
                      dangerouslySetInnerHTML={{
                        __html:
                          product?.description || 'No description available',
                      }}
                    />
                    <div className='mt-4'>
                      {product?.descriptors?.map((descriptor, index) => (
                        <Fragment key={index}>
                          <Link
                            href={`${Routes.collection}?globalFilter=${descriptor}`}
                            className='text-foreground hover:underline'
                          >
                            {descriptor}
                          </Link>
                          {index < product.descriptors.length - 1 && ' '}
                        </Fragment>
                      )) || 'No descriptors available'}
                    </div>
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>

            <Disclosure as='div'>
              {({ open }) => (
                <>
                  <h3>
                    <DisclosureButton className='group relative flex w-full items-center justify-between py-6 text-left'>
                      <span
                        className={clsx(
                          open ? 'text-foreground' : 'text-gray-900',
                          'text-sm font-medium',
                        )}
                      >
                        Details
                      </span>
                      <span className='ml-6 flex items-center'>
                        {open ? (
                          <MinusIcon
                            className='block h-6 w-6 text-indigo-400 group-hover:text-indigo-500'
                            aria-hidden='true'
                          />
                        ) : (
                          <PlusIcon
                            className='block h-6 w-6 text-gray-400 group-hover:text-gray-500'
                            aria-hidden='true'
                          />
                        )}
                      </span>
                    </DisclosureButton>
                  </h3>
                  <DisclosurePanel as='div' className='prose prose-sm pb-6'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4'>
                      <div>
                        <strong>Category: </strong>
                        {mapProductCategoryToString(product?.category)}
                      </div>
                      <div>
                        <strong>Sub-Category: </strong>
                        {mapProductSubCategoryToString(product?.subCategory)}
                      </div>
                      <div>
                        <strong>Materials: </strong>
                        {mapProductMaterialsToString(product?.materials)}
                      </div>
                      <div>
                        <strong>Size Type: </strong>
                        {mapProductSizeTypeToString(product?.sizeType)}
                      </div>
                      <div>
                        <strong>Numeric Size: </strong>
                        {product?.numericSize || 'N/A'}
                      </div>
                      <div>
                        <strong>Alpha Size: </strong>
                        {mapProductAlphaSizeToString(product?.alphaSize)}
                      </div>
                    </div>
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>

            <Disclosure as='div'>
              {({ open }) => (
                <>
                  <h3>
                    <DisclosureButton className='group relative flex w-full items-center justify-between py-6 text-left'>
                      <span
                        className={clsx(
                          open ? 'text-foreground' : 'text-gray-900',
                          'text-sm font-medium',
                        )}
                      >
                        Exact Sizing
                      </span>
                      <span className='ml-6 flex items-center'>
                        {open ? (
                          <MinusIcon
                            className='block h-6 w-6 text-indigo-400 group-hover:text-indigo-500'
                            aria-hidden='true'
                          />
                        ) : (
                          <PlusIcon
                            className='block h-6 w-6 text-gray-400 group-hover:text-gray-500'
                            aria-hidden='true'
                          />
                        )}
                      </span>
                    </DisclosureButton>
                  </h3>
                  <DisclosurePanel as='div' className='prose prose-sm pb-6'>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4'>
                      <div>
                        <strong>Chest/Bust: </strong>
                        {product?.chest || 'N/A'}
                      </div>
                      <div>
                        <strong>Length: </strong>
                        {product?.length || 'N/A'}
                      </div>
                      <div>
                        <strong>Sleeve: </strong>
                        {product?.sleeve || 'N/A'}
                      </div>
                      <div>
                        <strong>Waist: </strong>
                        {product?.waist || 'N/A'}
                      </div>
                      <div>
                        <strong>Rise: </strong>
                        {product?.rise || 'N/A'}
                      </div>
                      <div>
                        <strong>Inseam: </strong>
                        {product?.inseam || 'N/A'}
                      </div>
                      <div>
                        <strong>Shoulders Width: </strong>
                        {product?.shoulders || 'N/A'}
                      </div>
                      <div>
                        <strong>Width: </strong>
                        {product?.width || 'N/A'}
                      </div>
                      <div>
                        <strong>Height: </strong>
                        {product?.height || 'N/A'}
                      </div>
                      <div>
                        <strong>Depth: </strong>
                        {product?.depth || 'N/A'}
                      </div>
                      <div>
                        <strong>Shoulders Drop: </strong>
                        {product?.shoulderDrop || 'N/A'}
                      </div>
                      <div>
                        <strong>Heel Height: </strong>
                        {product?.heelHeight || 'N/A'}
                      </div>
                      <div>
                        <strong>Platform Height: </strong>
                        {product?.platformHeight || 'N/A'}
                      </div>
                      <div>
                        <strong>Diameter: </strong>
                        {product?.diameter || 'N/A'}
                      </div>
                      <div>
                        <strong>Ring Size: </strong>
                        {product?.ringSize || 'N/A'}
                      </div>
                    </div>
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
          </div>
        </section>
      </div>
    </>
  );
}
